import { supabase } from './SupabaseConfig';
import { Printer, PrinterRepository } from '@domain/pos/ports/PrinterRepository';
import { InfrastructureError } from '@core/errors/AppError';

export class SupabasePrinterRepository implements PrinterRepository {
  async getPrinters(uid: string, locationId: string): Promise<Printer[]> {
    try {
      const { data, error } = await supabase
        .from('printers')
        .select('*')
        .eq('owner_id', uid)
        .eq('location_id', locationId);

      if (error) throw error;
      return (data || []).map(this.mapPrinter);
    } catch (error) {
      throw new InfrastructureError('Lỗi lấy danh sách máy in', 'Supabase', error as Error);
    }
  }

  async addPrinter(uid: string, locationId: string, printer: Partial<Printer>): Promise<string> {
    try {
      const payload = {
        name: printer.name,
        connection_type: printer.connectionType,
        ip: printer.ip,
        port: printer.port,
        paper_size: printer.paperSize,
        bluetooth_name: printer.bluetoothName,
        tasks: printer.tasks,
        status: printer.status || 'active',
        owner_id: uid,
        location_id: locationId,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      const { data, error } = await supabase
        .from('printers')
        .insert(payload)
        .select()
        .single();

      if (error) throw error;
      return data.id;
    } catch (error) {
      throw new InfrastructureError('Lỗi thêm máy in mới', 'Supabase', error as Error);
    }
  }

  async updatePrinter(uid: string, locationId: string, printerId: string, updates: Partial<Printer>): Promise<void> {
    try {
      const payload: any = {
        updated_at: new Date().toISOString()
      };
      if (updates.name !== undefined) payload.name = updates.name;
      if (updates.connectionType !== undefined) payload.connection_type = updates.connectionType;
      if (updates.ip !== undefined) payload.ip = updates.ip;
      if (updates.port !== undefined) payload.port = updates.port;
      if (updates.paperSize !== undefined) payload.paper_size = updates.paperSize;
      if (updates.bluetoothName !== undefined) payload.bluetooth_name = updates.bluetoothName;
      if (updates.tasks !== undefined) payload.tasks = updates.tasks;
      if (updates.status !== undefined) payload.status = updates.status;

      const { error } = await supabase
        .from('printers')
        .update(payload)
        .eq('id', printerId)
        .eq('owner_id', uid);

      if (error) throw error;
    } catch (error) {
      throw new InfrastructureError('Lỗi cập nhật máy in', 'Supabase', error as Error);
    }
  }

  async deletePrinter(uid: string, locationId: string, printerId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('printers')
        .delete()
        .eq('id', printerId)
        .eq('owner_id', uid);

      if (error) throw error;
    } catch (error) {
      throw new InfrastructureError('Lỗi xóa máy in', 'Supabase', error as Error);
    }
  }

  listenPrinters(uid: string, locationId: string, callback: (printers: Printer[]) => void): () => void {
    const channel = supabase
      .channel(`printers-${locationId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'printers',
          filter: `owner_id=eq.${uid},location_id=eq.${locationId}`
        },
        async () => {
          const printers = await this.getPrinters(uid, locationId);
          callback(printers);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }

  private mapPrinter(d: any): Printer {
    return {
      id: d.id,
      name: d.name,
      connectionType: d.connection_type,
      ip: d.ip,
      port: d.port,
      paperSize: d.paper_size,
      bluetoothName: d.bluetooth_name,
      tasks: d.tasks || [],
      status: d.status,
      createdAt: d.created_at,
      updatedAt: d.updated_at
    };
  }

  // Bulk Config Implementation (Legacy Support)
  async getHardwareConfig(uid: string, locationId: string): Promise<{ printers: Printer[] }> {
    try {
      const { data, error } = await supabase
        .from('location_hardware_configs')
        .select('config_data')
        .eq('owner_id', uid)
        .eq('location_id', locationId)
        .maybeSingle();

      if (error) throw error;
      return { printers: data?.config_data?.printers || [] };
    } catch (error) {
      console.error('[SupabasePrinterRepo] getHardwareConfig failed:', error);
      return { printers: [] };
    }
  }

  async saveHardwareConfig(uid: string, locationId: string, config: { printers: Printer[] }): Promise<void> {
    try {
      const { error } = await supabase
        .from('location_hardware_configs')
        .upsert({
          owner_id: uid,
          location_id: locationId,
          config_data: config,
          updated_at: new Date().toISOString()
        }, { onConflict: 'owner_id,location_id' });

      if (error) throw error;
    } catch (error) {
      throw new InfrastructureError('Lỗi lưu cấu hình phần cứng', 'Supabase', error as Error);
    }
  }
}
